import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AssistantChat from "@/components/AssistantChat";
import ScheduledReports from "@/components/ScheduledReports";
import AnnouncementsCard from "@/components/AnnouncementsCard";
import OnShiftBoard from "@/components/OnShiftBoard";
import { useWelcome } from "@/components/WelcomeWidget";
import { DashboardTitle, BriefingStrip } from "@/components/dashboard/DashboardHero";
import { KpiTiles } from "@/components/dashboard/KpiTiles";
import { SalesPerformanceCard } from "@/components/dashboard/SalesPerformanceCard";
import { TrendAlerts } from "@/components/trends/TrendAlerts";
import ReturnsWeekWidget from "@/components/ReturnsWeekWidget";
import VehicleAlertsCard from "@/components/VehicleAlertsCard";
import { WidgetCard, WIDGET_GROUPS, EXTRA_KEYS } from "@/components/DashboardWidgets";
import { MyTodosCard } from "@/components/MyTodosCard";
import { MyNotesCard } from "@/components/MyNotesCard";
import { WeatherCard } from "@/components/WeatherCard";
import { QuickLinksCard } from "@/components/QuickLinksCard";
import { OpsBriefingCard } from "@/components/OpsBriefingCard";
import { WooSyncAlert } from "@/components/WooSyncAlert";
import { GitBranch, Settings, X, Plane, Loader2, GripHorizontal, Info, Compass, Search } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, rectSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip } from "recharts";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const CARD_DEFS = [
  { key: "sales_performance", label: "Sales performance", hint: "Monthly web and shop sales over the last 3, 6 or 12 months" },
  { key: "ops_briefing", label: "Operations Briefing", hint: "Ranked operational risks with recommended actions (managers)" },
  { key: "quick_links", label: "Quick links", hint: "Shortcuts to quotes, jobs, scheduler, stock and more" },
  { key: "returns_week", label: "Returns this week", hint: "Returns received/approved/quarantined/awaiting this week" },
  { key: "my_todos", label: "My Todos", hint: "Your open personal to-dos" },
  { key: "my_notes", label: "My Notes", hint: "Your latest personal notes" },
  { key: "weather", label: "Weather", hint: "7-day forecast for your company town" },
  { key: "workload", label: "Department workload", hint: "Jobs waiting per department queue" },
  { key: "expenses_month", label: "Expenses this month", hint: "Out-of-pocket spend this calendar month" },
  { key: "lead_sources", label: "Where jobs come from", hint: "Lead source breakdown of your jobs" },
  { key: "assistant", label: "AI Assistant", hint: "Ask questions & generate reports" },
  { key: "reports", label: "Scheduled Reports", hint: "Your saved recurring reports" },
  { key: "vehicle_alerts", label: "Vehicle Alerts", hint: "MOT & service warnings" },
  { key: "announcements", label: "Announcements", hint: "Company-wide notices" },
  { key: "on_shift", label: "On-Shift Board", hint: "Who's on shift right now" },
];
const CORE_KEYS = CARD_DEFS.map((c) => c.key);
const DEFAULT_ORDER = [...CORE_KEYS, ...EXTRA_KEYS];

const DEPT = {
  sales_performance: "Sales", lead_sources: "Sales", sales_snapshot: "Sales", unread_sms: "Sales",
  ops_briefing: "Operations", workload: "Operations", vehicle_alerts: "Operations", attention: "Operations", today_schedule: "Operations",
  jobs_at_risk: "Operations", engineer_status: "Operations",
  expenses_month: "Finance",
  on_shift: "HR", my_leave: "HR", cover_gaps: "HR", approvals: "HR", training_quals: "HR", attendance_overview: "HR",
  returns_week: "Stock", stock_alerts: "Stock", low_stock_reorder: "Stock", count_discrepancies: "Stock", slow_moving: "Stock", product_requests: "Stock",
  trustpilot: "Marketing", company_pulse: "Company", announcements: "Company",
  my_todos: "Personal", my_notes: "Personal", my_actions: "Personal", my_progress: "Personal",
  assistant: "AI", reports: "Reports", quick_links: "General", weather: "General",
};
const DEPT_TONE = {
  Sales: "bg-emerald-50 text-emerald-700 border-emerald-200", Operations: "bg-indigo-50 text-indigo-700 border-indigo-200",
  Finance: "bg-amber-50 text-amber-700 border-amber-200", HR: "bg-pink-50 text-pink-700 border-pink-200",
  Stock: "bg-sky-50 text-sky-700 border-sky-200", Marketing: "bg-violet-50 text-violet-700 border-violet-200",
  Company: "bg-orange-50 text-orange-700 border-orange-200", Personal: "bg-slate-50 text-slate-600 border-slate-200",
  AI: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200", Reports: "bg-teal-50 text-teal-700 border-teal-200", General: "bg-slate-50 text-slate-600 border-slate-200",
};

const CORE_LABELS = Object.fromEntries(CARD_DEFS.map((c) => [c.key, c.label]));
const EXTRA_HINTS = Object.fromEntries(WIDGET_GROUPS.flatMap((g) => g.items.map((i) => [i.key, i.hint])));
const INFO_BODY = {
  my_todos: "Your private personal to-do list, shared with nobody. Add a quick task inline or tick one off here; it stays in sync with the full My Tasks page where you can set due dates, priorities and link a to-do to a job, quote or customer.",
  my_notes: "Your private notebook — the latest notes you've jotted down, newest first. Click any note or ‘New’ to open the full My Notes page. Nothing here is shared with your team.",
  weather: "A 7-day forecast for your company's town, from your company profile address. Shows today's conditions plus daily highs, lows and chance of rain. Update the town on the company profile to change the location.",
  workload: "Each slice is a department queue, sized by how many jobs are waiting. Data comes live from the Jobs list (open jobs grouped by department, excluding Archive). Hover a slice for its count, or click a department or ‘View all jobs’ to open the filtered Jobs list.",
  expenses_month: "Total out-of-pocket expense claims dated in the current calendar month, from the Finance → Expenses ledger. Shows the running total plus how much is approved vs still pending sign-off.",
  lead_sources: "Where your jobs come from — each slice is a Lead Source (Google, referral, website, etc.), sized by how many jobs cite it. Built live from the Lead Source set on each job. Set a Lead Source when creating jobs to grow this picture and judge which marketing channels win work.",
  assistant: "Ask plain-English questions about your operations data — stock, sales, schedules — or tell it to build and email a report. Answers are generated from your live database and always respect your access permissions.",
  reports: "The recurring reports you asked the assistant to schedule. Reads your saved report schedules — switch them on or off, add recipients, or send one right now.",
  vehicle_alerts: "Vehicles with an MOT or service due or overdue, from the Plant Transport vehicle records (MOT/service dates and uploaded certificates). Click a row to open that vehicle.",
  announcements: "Company-wide notices posted by managers, from the Announcements records. Managers can post and pin; everyone else sees the latest here.",
  on_shift: "A live board of who is clocked in right now, built from today's shift clock-in activity. It refreshes automatically every minute.",
  returns_week: "This week's returns at a glance — received, approved, quarantined and awaiting — with a daily trend line. Built live from the Returns records. Click 'tap to inspect' to open the full Returns view.",

  attention: "A roundup of things needing action today — overdue jobs, unassigned work, stock shortages and open issues — pulled live from your Jobs, Scheduler and Stock records.",
  today_schedule: "Today's installs, deliveries, service visits and collections, counted from the shifts scheduled for today's date on the Scheduler.",
  jobs_at_risk: "Jobs that could slip because they're missing stock, forms, payment or an assigned engineer. Calculated from each job's fulfilment snapshot, its forms and its scheduled shifts.",
  engineer_status: "Where engineers are right now — travelling, on site, running late or available — derived from today's shifts and clock-in activity. ‘Available’ (est) is inferred from staff not on a shift today.",
  stock_alerts: "Low, reserved and soon-needed items, drawn from live per-location stock balances and the reservations held against upcoming jobs in Stock Control.",
  sales_snapshot: "This week's enquiries, quotes and orders, counted from the Sales/Jobs pipeline and imported web orders.",
  company_pulse: "A morale snapshot — jobs completed, customer feedback and recent wins — aggregated from completed jobs and customer reviews. Some figures are estimated (‘est’).",
  trustpilot: "The most recent public review for The Hot Tub Superstore, fetched from the store's Trustpilot profile and refreshed daily.",
  my_leave: "Your own holiday allowance, approved dates and pending requests, from the HR holiday records. Use ‘Request leave’ to submit a new one for approval.",
  my_actions: "Your outstanding personal tasks — forms to complete, announcements to read and timesheets — pulled from the work assigned to you.",
  my_progress: "Your personal stats — jobs completed, compliments and goals — based on jobs assigned to you. Several figures are estimated (‘est’).",
  cover_gaps: "Days over the next fortnight where approved leave thins out available cover, calculated from the team's holiday calendar. Managers/HR only.",
  approvals: "Requests waiting on you — leave, shift swaps, timesheets and expenses — from the HR approval queues. Managers/HR only.",
  training_quals: "Certificates expiring soon and overdue training, read from staff qualification records. Managers/HR only.",
  attendance_overview: "Absence and overtime trends this period, aggregated from timesheets and absence records and shown as totals only (respects access controls). Managers/HR only.",
  low_stock_reorder: "Products below their minimum level, with expected demand and incoming stock, from live warehouse balances and reorder data in Stock Control.",
  count_discrepancies: "Differences between scanned and recorded quantities from your most recent stock count on the warehouse floor. Run a count to populate it.",
  slow_moving: "Stock sitting idle with no reservations — idle, display and clearance lines — derived from stock age and movement history. Figures are estimated.",
  product_requests: "Parts requested from the warehouse but not yet fulfilled, across all jobs — counted live from the Product Requests queue. ‘Units still needed’ (est) is the outstanding shortfall.",
};
function widgetInfo(key, widgets) {
  const title = CORE_LABELS[key] || (widgets[key] && widgets[key].title) || key;
  const body = INFO_BODY[key] || EXTRA_HINTS[key] || "An at-a-glance summary card. Click any row to drill into the detail.";
  return { title, body };
}

const WORKLOAD_PALETTE = ["#6366f1", "#10b981", "#f59e0b", "#f43f5e", "#0ea5e9", "#8b5cf6", "#14b8a6", "#fb923c", "#ec4899", "#64748b"];

// Masonry row unit — must match the grid's gridAutoRows + gap (gap-6 = 24px).
const ROW_PX = 8;
const GAP_PX = 24;

function daysWaiting(ts) {
  if (!ts) return "—";
  const d = new Date(String(ts).replace(" ", "T"));
  if (isNaN(d)) return "—";
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "today";
  return `${days}d waiting`;
}

function Toggle({ on, onClick, testid }) {
  return (
    <button
      role="switch"
      aria-checked={on}
      onClick={onClick}
      data-testid={testid}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-300 ${on ? "bg-indigo-600" : "bg-slate-200"}`}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : "translate-x-1"}`} />
    </button>
  );
}

// A sortable masonry cell. Measures its own content to set a grid row-span so
// cards pack tightly (true masonry) while keeping a natural left→right order.
// Auto-hides (display:none, no grid cell) when its child renders nothing —
// the child stays mounted so its own data fetch still runs.
function SortableWidget({ id, empty, onEmpty, info, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const contentRef = useRef(null);
  const [span, setSpan] = useState(24);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const update = () => {
      onEmpty(id, el.childElementCount === 0);
      const h = el.getBoundingClientRect().height;
      if (h > 0) setSpan(Math.max(1, Math.ceil((h + GAP_PX) / (ROW_PX + GAP_PX))));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    const mo = new MutationObserver(update);
    mo.observe(el, { childList: true, subtree: true });
    return () => { ro.disconnect(); mo.disconnect(); };
  }, [id, onEmpty]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    gridRowEnd: `span ${span}`,
    zIndex: isDragging ? 40 : undefined,
    display: empty ? "none" : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className={`group relative ${isDragging ? "opacity-90" : ""}`} data-testid={`dash-widget-${id}`}>
      <div
        {...attributes}
        {...listeners}
        role="button"
        aria-label="Drag to reorder"
        title="Drag to reorder"
        data-testid={`dash-drag-${id}`}
        className="absolute left-1/2 -translate-x-1/2 -top-2 z-30 flex h-6 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-300 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing touch-none select-none hover:text-indigo-500"
      >
        <GripHorizontal className="w-4 h-4" />
      </div>
      {info && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              data-testid={`dash-info-${id}`}
              aria-label={`About ${info.title}`}
              title="What is this card?"
            className="absolute right-4 -top-2.5 z-20 flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 hover:text-indigo-600 hover:border-indigo-300 transition-colors"
            >
              <Info className="w-3.5 h-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent side="left" align="start" className="w-72" data-testid={`dash-info-content-${id}`}>
            <div className="text-sm font-bold text-slate-900">{info.title}</div>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">{info.body}</p>
          </PopoverContent>
        </Popover>
      )}
      {DEPT[id] && (
        <span className={`absolute left-4 -top-2.5 z-20 rounded-full border px-2 py-px text-[10px] font-semibold uppercase tracking-wide ${DEPT_TONE[DEPT[id]]}`} data-testid={`dash-tag-${id}`}>{DEPT[id]}</span>
      )}
      <div ref={contentRef}>{children}</div>
    </div>
  );
}

function WorkloadCard({ workload }) {
  const total = workload.reduce((a, q) => a + (q.count || 0), 0);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="dept-workload-card">
      <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
        <GitBranch className="w-4 h-4 text-indigo-500" /> Department workload
      </h2>
      <p className="text-xs text-slate-500 mt-0.5 mb-3">Jobs waiting in each queue you can access · spot bottlenecks at a glance.</p>
      <div className="w-full h-52 relative" data-testid="workload-donut">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={workload} dataKey="count" nameKey="department" cx="50%" cy="50%" innerRadius={62} outerRadius={90} paddingAngle={2} stroke="#ffffff" strokeWidth={2}>
              {workload.map((q, i) => <Cell key={q.department} fill={WORKLOAD_PALETTE[i % WORKLOAD_PALETTE.length]} />)}
            </Pie>
            <RTooltip formatter={(v, n) => [`${v} waiting`, n]} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-3xl font-bold text-slate-900 tabular-nums">{total.toLocaleString()}</span>
          <span className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Jobs waiting</span>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-0.5">
        {workload.map((q, i) => (
          <Link key={q.department} to="/tasks" data-testid={`workload-${q.department.toLowerCase()}`}
            className="flex items-center justify-between rounded-lg px-2.5 py-1.5 hover:bg-slate-50 transition-colors">
            <span className="flex items-center gap-2 text-sm text-slate-700 min-w-0">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: WORKLOAD_PALETTE[i % WORKLOAD_PALETTE.length] }} />
              <span className="truncate">{q.department}</span>
            </span>
            <span className="flex items-center gap-2 shrink-0">
              {q.oldest && <span className="text-[10px] text-amber-600 font-medium">{daysWaiting(q.oldest.created_at)}</span>}
              <span className="text-sm font-bold text-slate-900 tabular-nums w-10 text-right">{(q.count || 0).toLocaleString()}</span>
            </span>
          </Link>
        ))}
      </div>
      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
        <span className="text-sm text-slate-500">{workload.length} department{workload.length > 1 ? "s" : ""}</span>
        <Link to="/tasks" data-testid="workload-view-all" className="text-sm font-semibold text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1">
          View all jobs <span aria-hidden>→</span>
        </Link>
      </div>
    </div>
  );
}

function ExpensesMonthCard({ data }) {
  const total = data?.total || 0;
  const approved = data?.approved || 0;
  const pending = Math.max(0, total - approved);
  const gbp = (v) => `£${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="expenses-month-card">
      <h2 className="text-base font-bold text-slate-900 flex items-center gap-2"><Compass className="w-4 h-4 text-amber-500" /> Expenses this month</h2>
      <p className="text-xs text-slate-500 mt-0.5 mb-3">Out-of-pocket claims dated this calendar month.</p>
      <div className="text-3xl font-bold text-slate-900 tabular-nums">{gbp(total)}</div>
      <div className="text-xs text-slate-400 mt-0.5">{data?.count || 0} claim{(data?.count || 0) === 1 ? "" : "s"}</div>
      <div className="mt-4 space-y-1.5">
        <div className="flex items-center justify-between text-sm"><span className="text-emerald-600">Approved</span><span className="font-semibold tabular-nums">{gbp(approved)}</span></div>
        <div className="flex items-center justify-between text-sm"><span className="text-amber-600">Pending / declined</span><span className="font-semibold tabular-nums">{gbp(pending)}</span></div>
        <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden mt-1">
          <div className="h-full bg-emerald-500" style={{ width: `${total ? (approved / total) * 100 : 0}%` }} />
        </div>
      </div>
      <Link to="/finance/expenses" data-testid="expenses-month-link" className="inline-block mt-4 text-sm font-semibold text-indigo-600 hover:text-indigo-700">Manage expenses →</Link>
    </div>
  );
}

function LeadSourceCard({ data }) {
  const items = data?.items || [];
  const total = data?.total || 0;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="lead-source-card">
      <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
        <Compass className="w-4 h-4 text-indigo-500" /> Where jobs come from
      </h2>
      <p className="text-xs text-slate-500 mt-0.5 mb-3">Lead source of your jobs · see which channels win work.</p>
      {items.length === 0 ? (
        <div className="py-10 text-center" data-testid="lead-source-empty">
          <Compass className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-400 max-w-xs mx-auto">No lead sources recorded yet. Pick a <span className="font-medium text-slate-600">Lead Source</span> when creating a job and the breakdown will build here.</p>
          <Link to="/tasks" data-testid="lead-source-empty-cta" className="inline-block mt-3 text-sm font-semibold text-indigo-600 hover:text-indigo-700">Go to Jobs →</Link>
        </div>
      ) : (
        <>
          <div className="w-full h-52 relative" data-testid="lead-source-donut">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={items} dataKey="count" nameKey="source" cx="50%" cy="50%" innerRadius={62} outerRadius={90} paddingAngle={2} stroke="#ffffff" strokeWidth={2}>
                  {items.map((it, i) => <Cell key={it.source} fill={WORKLOAD_PALETTE[i % WORKLOAD_PALETTE.length]} />)}
                </Pie>
                <RTooltip formatter={(v, n) => [`${v} job${v === 1 ? "" : "s"}`, n]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-3xl font-bold text-slate-900 tabular-nums">{total.toLocaleString()}</span>
              <span className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Jobs</span>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-0.5">
            {items.map((it, i) => {
              const pct = total ? Math.round((it.count / total) * 100) : 0;
              return (
                <div key={it.source} data-testid={`lead-source-row-${i}`} className="flex items-center justify-between rounded-lg px-2.5 py-1.5">
                  <span className="flex items-center gap-2 text-sm text-slate-700 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: WORKLOAD_PALETTE[i % WORKLOAD_PALETTE.length] }} />
                    <span className="truncate">{it.source}</span>
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] text-slate-400 font-medium tabular-nums">{pct}%</span>
                    <span className="text-sm font-bold text-slate-900 tabular-nums w-10 text-right">{it.count.toLocaleString()}</span>
                  </span>
                </div>
              );
            })}
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
            <span className="text-sm text-slate-500">{items.length} source{items.length > 1 ? "s" : ""}</span>
            <Link to="/tasks" data-testid="lead-source-view-all" className="text-sm font-semibold text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1">View all jobs <span aria-hidden>→</span></Link>
          </div>
        </>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { can, cardAllowed } = useAuth();
  const [workload, setWorkload] = useState(null);
  const [leadSources, setLeadSources] = useState(null);
  const [expMonth, setExpMonth] = useState(null);
  const [scheduleRefresh, setScheduleRefresh] = useState(0);
  const [hidden, setHidden] = useState([]);
  const [shownExtra, setShownExtra] = useState([]);
  const [widgetOrder, setWidgetOrder] = useState([]);
  const [widgets, setWidgets] = useState({});
  const [availableRaw, setAvailable] = useState([]);
  const available = useMemo(() => availableRaw.filter(cardAllowed), [availableRaw, cardAllowed]);
  const cardDefs = useMemo(() => CARD_DEFS.filter((c) => cardAllowed(c.key)), [cardAllowed]);
  const [emptyKeys, setEmptyKeys] = useState({});
  const [panelOpen, setPanelOpen] = useState(false);
  const welcome = useWelcome();
  const [cardQ, setCardQ] = useState("");
  const matchCard = (c) => { const s = cardQ.trim().toLowerCase(); return !s || `${c.label} ${c.hint}`.toLowerCase().includes(s); };
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [kpis, setKpis] = useState(null);
  const [kpiTiles, setKpiTiles] = useState(null);
  const [tedOn, setTedOn] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const refreshWidgets = () => api.get("/dashboard/widgets").then((r) => { setWidgets(r.data.widgets || {}); setAvailable(r.data.available || []); }).catch(() => {});

  useEffect(() => {
    api.get("/tasks-departments/workload").then((r) => setWorkload((r.data.queues || []).filter((q) => q.department !== "Archive"))).catch(() => setWorkload([]));
    api.get("/dashboard/lead-sources").then((r) => setLeadSources(r.data)).catch(() => setLeadSources({ items: [], total: 0 }));
    if (can("expenses.view")) {
      api.get("/dashboard/expenses-month").then((r) => setExpMonth(r.data)).catch(() => setExpMonth({ total: 0, count: 0, approved: 0, pending: 0 }));
    }
    api.get("/me/dashboard-prefs").then((r) => { setHidden(r.data.hidden || []); setShownExtra(r.data.shown_extra || []); setWidgetOrder(r.data.widget_order || []); setKpiTiles(r.data.kpi_tiles || null); }).catch(() => {});
    api.get("/dashboard/kpis").then((r) => setKpis(r.data)).catch(() => setKpis({ kpis: {}, default: [], keys: [] }));
    api.get("/ted/status").then((r) => setTedOn(!!r.data.enabled)).catch(() => setTedOn(false));
    api.get("/dashboard/widgets").then((r) => { setWidgets(r.data.widgets || {}); setAvailable(r.data.available || []); }).catch(() => {});
  }, []);

  const isExtra = (key) => EXTRA_KEYS.includes(key);
  const show = (key) => (isExtra(key) ? shownExtra.includes(key) : !hidden.includes(key));

  const persist = (nextHidden, nextExtra, nextOrder) => {
    setHidden(nextHidden); setShownExtra(nextExtra); setWidgetOrder(nextOrder);
    api.put("/me/dashboard-prefs", { hidden: nextHidden, shown_extra: nextExtra, widget_order: nextOrder }).catch(() => {});
  };
  const toggle = (key) => {
    if (isExtra(key)) {
      persist(hidden, shownExtra.includes(key) ? shownExtra.filter((k) => k !== key) : [...shownExtra, key], widgetOrder);
    } else {
      persist(hidden.includes(key) ? hidden.filter((k) => k !== key) : [...hidden, key], shownExtra, widgetOrder);
    }
  };
  const saveKpis = (next) => {
    setKpiTiles(next);
    api.put("/me/dashboard-prefs", { hidden, shown_extra: shownExtra, widget_order: widgetOrder, kpi_tiles: next }).catch(() => {});
  };
  const reset = () => persist([], [], []);
  const showAll = () => persist([], EXTRA_KEYS.filter((k) => available.includes(k)), widgetOrder);
  const hiddenCount = cardDefs.filter((c) => !show(c.key)).length
    + WIDGET_GROUPS.flatMap((g) => g.items).filter((i) => available.includes(i.key) && !show(i.key)).length;

  const setEmpty = useCallback((id, val) => {
    setEmptyKeys((prev) => (prev[id] === val ? prev : { ...prev, [id]: val }));
  }, []);

  // Keys currently eligible to appear (parent-known visibility).
  const visibleKeys = useMemo(() => {
    return DEFAULT_ORDER.filter((k) => {
      if (!cardAllowed(k)) return false;
      if (k === "workload") return show("workload") && workload && workload.length > 0;
      if (CORE_KEYS.includes(k)) return show(k);
      return available.includes(k) && shownExtra.includes(k) && !!widgets[k];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidden, shownExtra, available, widgets, workload, cardAllowed]);

  // Saved order first, then any newly-visible keys appended in default order.
  const displayOrder = useMemo(() => {
    const ordered = widgetOrder.filter((k) => visibleKeys.includes(k));
    const rest = visibleKeys.filter((k) => !widgetOrder.includes(k));
    return [...ordered, ...rest];
  }, [widgetOrder, visibleKeys]);

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldI = displayOrder.indexOf(active.id);
    const newI = displayOrder.indexOf(over.id);
    if (oldI < 0 || newI < 0) return;
    const next = arrayMove(displayOrder, oldI, newI);
    const merged = [...next, ...widgetOrder.filter((k) => !next.includes(k))];
    setWidgetOrder(merged);
    api.put("/me/dashboard-prefs", { hidden, shown_extra: shownExtra, widget_order: merged }).catch(() => {});
  };

  const renderWidget = (key) => {
    switch (key) {
      case "sales_performance": return can("finance.view") ? <SalesPerformanceCard /> : null;
      case "quick_links": return <QuickLinksCard />;
      case "ops_briefing": return <OpsBriefingCard />;
      case "returns_week": return <ReturnsWeekWidget />;
      case "my_todos": return <MyTodosCard />;
      case "my_notes": return <MyNotesCard />;
      case "weather": return <WeatherCard />;
      case "workload": return workload && workload.length > 0 ? <WorkloadCard workload={workload} /> : null;
      case "lead_sources": return <LeadSourceCard data={leadSources} />;
      case "expenses_month": return can("expenses.view") ? <ExpensesMonthCard data={expMonth} /> : null;
      case "assistant": return <AssistantChat onScheduled={() => setScheduleRefresh((n) => n + 1)} />;
      case "reports": return <ScheduledReports refreshKey={scheduleRefresh} />;
      case "vehicle_alerts": return <VehicleAlertsCard />;
      case "announcements": return <AnnouncementsCard />;
      case "on_shift": return <OnShiftBoard />;
      default: return widgets[key] ? <WidgetCard w={widgets[key]} onAction={(m) => { if (m === "leave") setLeaveOpen(true); }} /> : null;
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="page-dashboard">
      <div className="flex flex-wrap items-start gap-3 mb-6">
        <div className="flex-1 min-w-[220px]">
          <DashboardTitle />
        </div>
        <button
          onClick={() => setPanelOpen(true)}
          data-testid="dashboard-customise-btn"
          title="Customise dashboard"
          aria-label="Customise dashboard"
          className="w-10 h-10 rounded-xl border border-slate-200 text-slate-500 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50 flex items-center justify-center transition-colors shrink-0"
        >
          <Settings className="w-5 h-5" />
        </button>
      </div>

      <WooSyncAlert />
      <TrendAlerts />

      <div className="space-y-4 mb-8">
        <BriefingStrip data={welcome.data} loading={welcome.loading} tedEnabled={tedOn} />
        <KpiTiles data={kpis} chosen={kpiTiles} onChange={saveKpis} />
      </div>

      {hiddenCount > 0 && (
        <div className="mb-4 text-xs text-slate-400" data-testid="dashboard-hidden-note">
          {hiddenCount} card{hiddenCount > 1 ? "s" : ""} hidden ·{" "}
          <button onClick={() => setPanelOpen(true)} className="text-indigo-600 hover:underline">customise</button>
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={displayOrder} strategy={rectSortingStrategy}>
          <div
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
            style={{ gridAutoRows: `${ROW_PX}px`, alignItems: "start" }}
            data-testid="dashboard-grid"
          >
            {displayOrder.map((key) => (
              <SortableWidget key={key} id={key} info={widgetInfo(key, widgets)} empty={!!emptyKeys[key]} onEmpty={setEmpty}>
                {renderWidget(key)}
              </SortableWidget>
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {leaveOpen && <LeaveRequestModal onClose={() => setLeaveOpen(false)} onSaved={refreshWidgets} />}

      {/* Customise slide-over */}
      {panelOpen && (
        <div className="fixed inset-0 z-50" data-testid="dashboard-customise-overlay">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setPanelOpen(false)} />
          <aside className="absolute right-0 top-0 h-full w-full max-w-sm bg-white shadow-2xl flex flex-col animate-in slide-in-from-right" data-testid="dashboard-customise-panel">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div>
                <h2 className="text-base font-bold text-slate-900">Customise dashboard</h2>
                <p className="text-xs text-slate-500 mt-0.5">Choose which cards you see · drag cards on the page to reorder.</p>
              </div>
              <button onClick={() => setPanelOpen(false)} aria-label="Close" data-testid="dashboard-customise-close" className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 pt-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input value={cardQ} onChange={(e) => setCardQ(e.target.value)} placeholder="Search cards…" data-testid="dash-card-search"
                  className="w-full h-9 pl-9 pr-8 rounded-full bg-slate-100 text-sm outline-none focus:ring-2 focus:ring-indigo-200" />
                {cardQ && <button onClick={() => setCardQ("")} aria-label="Clear search" data-testid="dash-card-search-clear" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              {cardDefs.some(matchCard) && <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Core cards</div>}
              {cardDefs.filter(matchCard).map((c) => (
                <div key={c.key} className="flex items-center gap-3 rounded-xl px-3 py-3 hover:bg-slate-50">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800">{c.label}</div>
                    <div className="text-[11px] text-slate-400 truncate">{c.hint}</div>
                  </div>
                  <Toggle on={show(c.key)} onClick={() => toggle(c.key)} testid={`dash-toggle-${c.key}`} />
                </div>
              ))}
              {WIDGET_GROUPS.map((g) => {
                const items = g.items.filter((i) => available.includes(i.key) && matchCard(i));
                if (items.length === 0) return null;
                return (
                  <div key={g.cat}>
                    <div className="px-2 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{g.cat}</div>
                    {items.map((c) => (
                      <div key={c.key} className="flex items-center gap-3 rounded-xl px-3 py-3 hover:bg-slate-50">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-800">{c.label}</div>
                          <div className="text-[11px] text-slate-400 truncate">{c.hint}</div>
                        </div>
                        <Toggle on={show(c.key)} onClick={() => toggle(c.key)} testid={`dash-toggle-${c.key}`} />
                      </div>
                    ))}
                  </div>
                );
              })}
              {cardQ && !cardDefs.some(matchCard) && !WIDGET_GROUPS.some((g) => g.items.some((i) => available.includes(i.key) && matchCard(i))) && (
                <div className="px-3 py-10 text-center text-sm text-slate-400" data-testid="dash-card-search-empty">No cards match “{cardQ}”</div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button onClick={reset} data-testid="dash-prefs-reset" className="text-sm text-slate-500 hover:text-indigo-600 hover:underline">Reset to default</button>
                <button onClick={showAll} disabled={hiddenCount === 0} data-testid="dash-prefs-show-all" className="text-sm font-medium text-indigo-600 hover:underline disabled:text-slate-300 disabled:no-underline">Show all</button>
              </div>
              <button onClick={() => setPanelOpen(false)} data-testid="dash-prefs-done" className="rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2">Done</button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}


function LeaveRequestModal({ onClose, onSaved }) {
  const [f, setF] = useState({ start_date: "", end_date: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState(null);
  const [me, setMe] = useState(null);

  const loadMine = () => api.get("/holidays").then((r) => {
    setMe(r.data.me);
    setMine((r.data.items || []).filter((h) => h.user_id === r.data.me).sort((a, b) => (b.start_date || "").localeCompare(a.start_date || "")));
  }).catch(() => setMine([]));
  useEffect(() => { loadMine(); }, []);

  const submit = async () => {
    if (!f.start_date || !f.end_date) { toast.error("Pick start and end dates"); return; }
    setBusy(true);
    try { await api.post("/holidays", f); toast.success("Leave requested — sent for approval"); setF({ start_date: "", end_date: "", note: "" }); await loadMine(); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };
  const cancel = async (h) => {
    if (!window.confirm("Cancel this leave request?")) return;
    try { await api.delete(`/holidays/${h.id}`); toast.success("Request cancelled"); await loadMine(); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };
  const STATUS_CLS = { approved: "bg-emerald-50 text-emerald-700 border-emerald-200", pending: "bg-amber-50 text-amber-700 border-amber-200", declined: "bg-rose-50 text-rose-600 border-rose-200" };
  const fmt = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/40 flex justify-end" onClick={onClose} data-testid="leave-request-modal">
      <div className="bg-white h-full w-full sm:w-1/2 sm:max-w-none shadow-2xl flex flex-col animate-in slide-in-from-right" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Plane className="w-5 h-5 text-indigo-500" /> My leave</h3>
          <button onClick={onClose} aria-label="Close" data-testid="leave-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4 flex-1 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Start</span>
              <Input type="date" value={f.start_date} onChange={(e) => setF((s) => ({ ...s, start_date: e.target.value }))} className="mt-1" data-testid="leave-start" /></label>
            <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">End</span>
              <Input type="date" value={f.end_date} onChange={(e) => setF((s) => ({ ...s, end_date: e.target.value }))} className="mt-1" data-testid="leave-end" /></label>
          </div>
          <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Note (optional)</span>
            <textarea value={f.note} onChange={(e) => setF((s) => ({ ...s, note: e.target.value }))} rows={2} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100" data-testid="leave-note" /></label>
          <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={submit} data-testid="leave-submit">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Submit request</Button>

          <div className="pt-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">My requests</div>
            {mine === null ? <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
              : mine.length === 0 ? <div className="text-sm text-slate-400" data-testid="leave-none">No requests yet.</div>
              : (
                <div className="space-y-2" data-testid="leave-my-requests">
                  {mine.map((h) => (
                    <div key={h.id} className="rounded-xl border border-slate-200 p-3" data-testid={`leave-req-${h.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-slate-800 font-medium">{fmt(h.start_date)} → {fmt(h.end_date)}</span>
                        <span className={`text-[11px] uppercase tracking-wide font-semibold rounded-full border px-2 py-0.5 ${STATUS_CLS[h.status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{h.status}</span>
                      </div>
                      {h.note && <div className="text-xs text-slate-500 mt-1">“{h.note}”</div>}
                      {h.status === "pending" && (
                        <button onClick={() => cancel(h)} className="mt-2 text-xs font-medium text-rose-600 hover:underline" data-testid={`leave-cancel-${h.id}`}>Cancel request</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>
        <div className="px-5 py-4 border-t border-slate-100 flex justify-end">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}
